/* V-NEXUS Drop Hero — continuous coverflow + pointer depth. No dependencies.
   pos is a float index that drifts forward; every frame each card is placed by its
   distance from pos. Hover pauses the drift, leaving resumes it. */
if (!customElements.get('vn-hero')) {
  customElements.define(
    'vn-hero',
    class VnHero extends HTMLElement {
      connectedCallback() {
        this.cards = [...this.querySelectorAll('[data-card]')];
        this.metas = [...this.querySelectorAll('[data-meta]')];
        this.dots = [...this.querySelectorAll('[data-dot]')];
        this.count = this.cards.length;
        if (!this.count) return;

        const seconds = Number(this.dataset.autoplay) || 0;
        this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.speed = seconds && !this.reduced ? 1 / seconds : 0; // items per second
        this.velocity = this.speed;
        this.pos = 0;
        this.target = null;
        this.hovered = false;
        this.active = -1;
        this.mobile = matchMedia('(max-width: 749px)');

        this.tick = this.tick.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onKey = this.onKey.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onMove = this.onMove.bind(this);
        // Pause only while the pointer is over a product card or its details.
        this.onOver = (event) => {
          const on = Boolean(event.target.closest('[data-card], .vn-drop__info'));
          if (on === this.hovered) return;
          this.hovered = on;
          if (!on) this.start();
        };
        this.onLeave = () => {
          this.hovered = false;
          this.start();
        };
        this.onBlockSelect = (event) => {
          const i = this.cards.findIndex((card) => card.contains(event.target));
          if (i > -1) this.moveTo(i);
        };

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKey);
        this.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.addEventListener('pointerup', this.onPointerUp, { passive: true });
        this.addEventListener('pointerover', this.onOver, { passive: true });
        this.addEventListener('pointerleave', this.onLeave, { passive: true });
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        if (!this.reduced && this.dataset.tilt === 'true' && matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.addEventListener('pointermove', this.onMove, { passive: true });
        }

        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          if (this.visible) this.start();
        });
        this.observer.observe(this);

        this.render();
        requestAnimationFrame(() => this.classList.add('is-ready'));
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        cancelAnimationFrame(this.raf);
        cancelAnimationFrame(this.frame);
      }

      start() {
        if (this.raf) return;
        this.last = performance.now();
        this.raf = requestAnimationFrame(this.tick);
      }

      tick(now) {
        this.raf = null;
        if (!this.visible) return;

        const dt = Math.min((now - this.last) / 1000, 0.1);
        this.last = now;

        if (this.target !== null) {
          const gap = this.target - this.pos;
          this.pos += this.reduced ? gap : gap * Math.min(1, dt * 6);
          if (Math.abs(gap) < 0.002) {
            this.pos = this.target;
            this.target = null;
          }
        } else {
          const goal = this.hovered ? 0 : this.speed;
          this.velocity += (goal - this.velocity) * Math.min(1, dt * 4); // ease to stop / resume
          this.pos += this.velocity * dt;
          // Once hover has slowed the drift, settle the nearest card into the centre.
          if (this.hovered && this.velocity < 0.03 && Math.abs(this.pos - Math.round(this.pos)) > 0.002) {
            this.velocity = 0;
            this.target = Math.ceil(this.pos - 0.25);
          }
        }

        this.render();
        if (this.target !== null || this.velocity > 0.0005 || (!this.hovered && this.speed)) {
          this.raf = requestAnimationFrame(this.tick);
        }
      }

      // Signed distance of card i from the current position, wrapped to [-count/2, count/2).
      offset(i) {
        const n = this.count;
        let d = (((i - this.pos) % n) + n) % n;
        if (d >= n / 2) d -= n;
        return d;
      }

      render() {
        const small = this.mobile.matches;
        const spread = small ? 58 : 68;
        const turn = small ? 42 : 38;
        const sideOpacity = small ? 0.4 : 0.55;

        this.cards.forEach((card, i) => {
          const d = this.offset(i);
          const a = Math.abs(d);
          const near = Math.min(a, 1);
          const far = Math.max(a - 1, 0);
          const x = Math.sign(d) * (near * spread + far * 47);
          const z = -(near * 260 + far * 260);
          const r = -Math.sign(d) * (near * turn + far * 12);
          const o = a <= 1 ? 1 - near * (1 - sideOpacity) : Math.max(0, sideOpacity - far * (small ? sideOpacity : 0.37));

          card.style.transform = 'translateX(' + x + '%) translateZ(' + z + 'px) rotateY(' + r + 'deg)';
          card.style.opacity = o.toFixed(3);
          card.style.zIndex = String(10 - Math.round(a * 2));
          card.dataset.pos = String(Math.max(-2, Math.min(2, Math.round(d))));
        });

        const active = ((Math.round(this.pos) % this.count) + this.count) % this.count;
        if (active !== this.active) this.setActive(active);
      }

      setActive(index) {
        this.active = index;
        this.cards.forEach((card, i) => {
          const on = i === index;
          card.setAttribute('aria-hidden', String(!on));
          card.querySelector('a')?.setAttribute('tabindex', on ? '0' : '-1');
        });
        this.metas.forEach((meta, i) => (meta.hidden = i !== index));
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
      }

      moveTo(index) {
        this.target = this.pos + this.offset(index);
        this.start();
      }

      moveBy(step) {
        this.target = Math.round(this.target ?? this.pos) + step;
        this.start();
      }

      onClick(event) {
        if (event.target.closest('[data-prev]')) return this.moveBy(-1);
        if (event.target.closest('[data-next]')) return this.moveBy(1);

        const dot = event.target.closest('[data-dot]');
        if (dot) return this.moveTo(this.dots.indexOf(dot));

        const card = event.target.closest('[data-card]');
        if (!card) return;
        const i = this.cards.indexOf(card);
        if (this.swiped || i !== this.active) {
          event.preventDefault();
          if (!this.swiped) this.moveTo(i);
        }
        this.swiped = false;
      }

      onKey(event) {
        if (event.key === 'ArrowLeft') this.moveBy(-1);
        if (event.key === 'ArrowRight') this.moveBy(1);
      }

      onPointerDown(event) {
        this.startX = event.clientX;
        this.swiped = false;
      }

      onPointerUp(event) {
        const dx = event.clientX - (this.startX ?? event.clientX);
        if (Math.abs(dx) > 40) {
          this.swiped = true;
          this.moveBy(dx < 0 ? 1 : -1);
        }
      }

      onMove(event) {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          const rect = this.getBoundingClientRect();
          this.style.setProperty('--px', (((event.clientX - rect.left) / rect.width - 0.5) * 2).toFixed(3));
          this.style.setProperty('--py', (((event.clientY - rect.top) / rect.height - 0.5) * 2).toFixed(3));
        });
      }
    }
  );
}
